import { Injectable } from '@angular/core'
import { BiddingItemEntity, WinningBidEntity, BidItemTypeEnum, ItemTypeEnum } from 'libs/gb-front/item/data-access/src'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { combineLatest, map, Observable } from 'rxjs'

@Injectable({
  providedIn: 'root',
})
export class GbBiddingItemHelperService {
  constructor(private authFacade: AuthFacade) {}

  getCurrentBidAmount(biddingItem: BiddingItemEntity | null): Observable<number> {
    return combineLatest([this.authFacade.isVolunteer$, this.authFacade.authProfileId$]).pipe(
      map(([isVolunteer, authProfileId]) => {
        // sealed bidding item
        if (biddingItem?.sealed_bid_item) {
          return this.getSealedBidAmount(biddingItem, authProfileId, isVolunteer)
        }
        // bidding item with bids
        if (this.hasBids(biddingItem)) {
          const profileWinningBid = this.getProfileWinningBid(biddingItem, authProfileId, isVolunteer)

          if (profileWinningBid && this.isMultipleUnitsAvailable(biddingItem)) {
            return profileWinningBid.amount
          }
          if (!this.isMultipleUnitsAvailable(biddingItem)) {
            return this.getHighestBid(biddingItem)!.amount
          }
          if (biddingItem?.units_available && biddingItem.units_available <= biddingItem.winning_bids!.length) {
            return this.getLowestBid(biddingItem)!.minimum_next_bid
          }
          return biddingItem?.minimum_next_bid!
        }
        return biddingItem?.starting_price!
      }),
    )
  }

  getMinBidAmount(biddingItem: BiddingItemEntity | null | undefined): Observable<number> {
    return combineLatest([this.authFacade.isVolunteer$, this.authFacade.authProfileId$]).pipe(
      map(([isVolunteer, authProfileId]) => {
        if (!biddingItem) return 0

        if (biddingItem.sealed_bid_item) {
          return this.getSealedBidAmount(biddingItem, authProfileId, isVolunteer)
        }

        if (this.hasBids(biddingItem)) {
          const profileWinningBid = this.getProfileWinningBid(biddingItem, authProfileId, isVolunteer)

          // If the user is part of the winning bids, return their next min bid
          if (profileWinningBid) {
            return profileWinningBid.minimum_next_bid
          }

          return biddingItem.minimum_next_bid
        }
        return biddingItem.starting_price
      }),
    )
  }

  getSealedBidAmount(
    biddingItem: BiddingItemEntity,
    authProfileId: string | undefined | null,
    isVolunteer: boolean,
  ): number {
    const profileWinningBid = this.getProfileWinningBid(biddingItem, authProfileId, isVolunteer)

    if (profileWinningBid) {
      return profileWinningBid.minimum_next_bid
    }
    return biddingItem.starting_price!
  }

  getProfileWinningBid(
    biddingItem: BiddingItemEntity | null,
    authProfileId: string | number | null | undefined,
    isVolunteer?: boolean | null,
  ): WinningBidEntity | null {
    if (isVolunteer || !authProfileId || !this.hasBids(biddingItem)) {
      return null
    }
    const profileWinningBid = biddingItem?.winning_bids!.find((bid) => bid.profile_uuid === authProfileId)
    return profileWinningBid ? profileWinningBid : null
  }

  isLiveBiddingItem(biddingItem: BiddingItemEntity | null): boolean {
    return !!(biddingItem && biddingItem.live_auction_item && biddingItem.enable_live_auction)
  }

  winningBidMeetsReserve(winningBid: WinningBidEntity | null, biddingItem: BiddingItemEntity): boolean {
    if (!winningBid) return false
    // return true if reserve price not enabled
    if (!biddingItem.reserve_price_enabled) return true
    // return if winning bid amount is meets reserve if enabled
    return winningBid.amount >= biddingItem.reserve_price
  }

  hasBids = (biddingItem: BiddingItemEntity | null): boolean =>
    !!biddingItem?.winning_bids && biddingItem.winning_bids.length > 0

  isMultipleUnitsAvailable = (biddingItem: BiddingItemEntity | null): boolean =>
    !!biddingItem?.units_available && biddingItem.units_available > 1

  getHighestBid = (biddingItem: BiddingItemEntity | null): WinningBidEntity | null | undefined =>
    this.hasBids(biddingItem) ? biddingItem?.winning_bids![0] : null

  getLowestBid = (biddingItem: BiddingItemEntity): WinningBidEntity | null =>
    this.hasBids(biddingItem) ? biddingItem.winning_bids![biddingItem.winning_bids!.length - 1] : null

  getBiddingItemType(biddingItem: BiddingItemEntity | null): string {
    if (biddingItem?.live_auction_item) {
      return BidItemTypeEnum.Live
    } else if (biddingItem?.sealed_bid_item) {
      return BidItemTypeEnum.Sealed
    } else {
      return ItemTypeEnum.Bidding
    }
  }
}
